import React, { ReactNode, useCallback, useMemo } from 'react';
import TagIcon from 'icons/Tag.icon';
import CalculatorIcon from 'icons/Calculator.icon';
import DiscountIcon from 'icons/Discount.icon';
import { ASKING_PRICE_SELECT } from 'models/DealRoom';
import { Listing } from 'models/Listing';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import { useTranslation } from 'react-i18next';
import { getIntegerValue } from './utils';
import { mapValueToMoneyFormat } from 'utils';
import useShowFieldVisibleInfo from 'hooks/useShowFieldVisibleInfo';

type KeyDetailsOption = {
  icon?: ReactNode;
  text: string;
  value?: string | number;
  popupText?: string;
  popupIcon?: ReactNode;
};

export default function useGetKeyDetailsOptions(
  listing: Partial<Listing>,
  isFixed: boolean,
  isEOI: boolean,
  adjEBITDA: number,
) {
  const { t } = useTranslation();

  const getShowFieldVisibleInfo = useShowFieldVisibleInfo();

  const getMoneyValue = useCallback((value: string | number) => {
    if (!value) return null;
    return mapValueToMoneyFormat(getIntegerValue(value));
  }, []);

  const priceVisibleInfo =
    listing?.askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE
      ? listing?.amountRequiredVisibleInfo
      : listing?.amountFromRequiredVisibleInfo ||
        listing?.amountToRequiredVisibleInfo;

  const { isHidden: isPriceHidden, reasonToHideContent: priceReason } =
    getShowFieldVisibleInfo(priceVisibleInfo);

  const { isHidden: isRevenueHidden, reasonToHideContent: revenueReason } =
    getShowFieldVisibleInfo(listing?.revenueRequiredVisibleInfo);

  const { isHidden: isEBITDAHidden, reasonToHideContent: EBITDAReason } =
    getShowFieldVisibleInfo(listing?.EBITDARequiredVisibleInfo);

  return useMemo(
    () =>
      [
        {
          icon: <TagIcon />,
          text: 'priceGuide',
          value: isEOI
            ? t('eoi')
            : (isFixed
                ? getMoneyValue(listing?.amount)
                : getMoneyValue(listing?.amountFrom)) ||
              (isPriceHidden ? priceReason : null),
        },
        {
          icon: <CalculatorIcon />,
          text: 'revenue',
          value:
            getMoneyValue(listing?.revenue) ||
            (isRevenueHidden ? revenueReason : null),
        },
        {
          icon: <DiscountIcon />,
          text: 'adjEBITDA',
          popupText: 'adjEBITDATooltip',
          value:
            getMoneyValue(adjEBITDA) ||
            (isEBITDAHidden ? EBITDAReason : null),
          popupIcon: <InformationcircleIcon />,
        },
      ] as KeyDetailsOption[],
    [
      adjEBITDA,
      isEOI,
      isFixed,
      listing,
      getMoneyValue,
      t,
      isPriceHidden,
      priceReason,
      isRevenueHidden,
      revenueReason,
      isEBITDAHidden,
      EBITDAReason,
    ],
  );
}
